import { Sparkles, Gauge, MessageCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardHeader } from '@/components/ui/Card';
import { Alert } from '@/components/ui/Alert';
import { ToggleRow } from '@/components/ui/ToggleRow';
import { SaveBar } from '@/components/ui/SaveBar';
import { Button } from '@/components/ui/Button';
import { getGuildConfig, patchGuildConfig, resetGuildConfig } from '@/lib/api-client/guild-config';
import { ApiError } from '@/lib/api-client/client';

/** Fields this page lets the user edit. ONLY these are ever sent in a PATCH
 * (see buildChanges below) - the loaded settings also contain fields with
 * no control on this page (allowed_channels, allowed_roles, log_channel,
 * cooldown_seconds, ...), and echoing those back would be pointless at
 * best and, if any went stale (a since-deleted channel), would make an
 * unrelated toggle save fail. Sending only what changed also means the
 * request can never contain a field the bot's writable list doesn't have. */
const EDITABLE_FIELDS = ['enabled', 'memory_enabled', 'deepthink_enabled', 'nsfw_restricted'] as const;
type EditableField = (typeof EDITABLE_FIELDS)[number];

function buildChanges(original: AiGuildSettings, draft: AiGuildSettings): Partial<Pick<AiGuildSettings, EditableField>> {
  const changes: Partial<Pick<AiGuildSettings, EditableField>> = {};
  for (const field of EDITABLE_FIELDS) {
    if (draft[field] !== original[field]) {
      changes[field] = draft[field];
    }
  }
  return changes;
}

/** Matches ai_guild_settings' guild-safe fields exactly (ai/settings.py) —
 * NOT the full row; provider keys/models/tiers never appear here, they
 * don't exist on this table at all (see bridge/config_registry.py's
 * AI_WRITABLE_FIELDS and docs/BOT_BRIDGE.md). */
interface AiGuildSettings {
  enabled: boolean;
  memory_enabled: boolean;
  deepthink_enabled: boolean;
  cooldown_seconds: number;
  response_char_limit: number | null;
  logging_enabled: boolean;
  /** Discord ids arrive as strings (bridge/config_registry.stringify_snowflakes) - never numbers. */
  log_channel: string | null;
  nsfw_restricted: boolean;
  allowed_channels: string[] | null;
  allowed_roles: string[] | null;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'forbidden' }
  | { kind: 'ready'; settings: AiGuildSettings };

export function AiPage() {
  const { guildId } = useParams<{ guildId: string }>();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [draft, setDraft] = useState<AiGuildSettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    if (!guildId) return;
    let cancelled = false;

    setState({ kind: 'loading' });
    getGuildConfig<AiGuildSettings>(guildId, 'ai')
      .then((res) => {
        if (cancelled) return;
        const settings = res.data as AiGuildSettings;
        setState({ kind: 'ready', settings });
        setDraft(settings);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.code === 'FORBIDDEN') {
          setState({ kind: 'forbidden' });
          return;
        }
        if (err instanceof ApiError && (err.code === 'BOT_UNREACHABLE' || err.code === 'BOT_DEGRADED')) {
          setState({ kind: 'error', message: 'The bot is temporarily unreachable — settings will load once it reconnects.' });
          return;
        }
        setState({ kind: 'error', message: err instanceof ApiError ? err.message : 'Something went wrong loading these settings.' });
      });

    return () => {
      cancelled = true;
    };
  }, [guildId]);

  if (state.kind === 'loading') {
    return <div className="text-small text-text-tertiary p-6">Loading…</div>;
  }

  if (state.kind === 'forbidden') {
    return (
      <div className="max-w-2xl">
        <Alert tone="danger" title="You don't have permission to view this" description="Dem AI settings require the Manage Server permission in this Discord server." />
      </div>
    );
  }

  if (state.kind === 'error') {
    return (
      <div className="max-w-2xl">
        <Alert tone="danger" title="Couldn't load AI settings" description={state.message} />
      </div>
    );
  }

  const current = draft ?? state.settings;
  const pendingChanges = buildChanges(state.settings, current);
  const dirty = Object.keys(pendingChanges).length > 0;

  const update = (changes: Partial<AiGuildSettings>) => {
    setDraft({ ...current, ...changes });
  };

  const handleSave = async () => {
    if (!guildId) return;
    setSaving(true);
    setSaveError(null);
    try {
      const res = await patchGuildConfig<AiGuildSettings>(guildId, 'ai', pendingChanges);
      const settings = res.data as AiGuildSettings;
      setState({ kind: 'ready', settings });
      setDraft(settings);
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : 'Could not save — please try again.');
    } finally {
      setSaving(false);
    }
  };

  // POST /reset: the bot deletes this server's override row (ai/settings.py
  // reset_settings), so the values that come back ARE the bot's canonical
  // defaults - nothing here decides or hard-codes what "default" means.
  const handleReset = async () => {
    if (!guildId) return;
    if (!window.confirm('Reset all Dem AI settings for this server to their defaults?')) return;
    setResetting(true);
    setSaveError(null);
    try {
      const res = await resetGuildConfig<AiGuildSettings>(guildId, 'ai');
      const settings = res.data as AiGuildSettings;
      setState({ kind: 'ready', settings });
      setDraft(settings);
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : 'Could not reset — please try again.');
    } finally {
      setResetting(false);
    }
  };

  const handleDiscard = () => {
    setDraft(state.settings);
    setSaveError(null);
  };

  return (
    <div className="max-w-2xl space-y-6 pb-24">
      <div>
        <h1 className="text-h1 text-text-primary mb-1">Dem AI</h1>
        <p className="text-small text-text-secondary">
          Per-guild settings for the AI assistant. Provider diagnostics live in the Owner Console.
        </p>
      </div>

      {saveError && <Alert tone="danger" title="Couldn't save" description={saveError} />}

      <Card>
        <CardHeader title="Assistant" action={<Sparkles size={18} className="text-text-tertiary" aria-hidden="true" />} />
        <div className="divide-y divide-border-subtle">
          <ToggleRow
            title="Enable Dem AI in this server"
            checked={current.enabled}
            onChange={(v) => update({ enabled: v })}
          />
          <ToggleRow
            title="Conversation memory"
            description="Lets /ai chat remember recent turns within a channel."
            checked={current.memory_enabled}
            onChange={(v) => update({ memory_enabled: v })}
          />
          <ToggleRow
            title="DeepThink"
            description="Allows the slower, higher-quality multi-provider reasoning mode."
            checked={current.deepthink_enabled}
            onChange={(v) => update({ deepthink_enabled: v })}
          />
          <ToggleRow
            title="Restrict to non-NSFW channels"
            checked={current.nsfw_restricted}
            onChange={(v) => update({ nsfw_restricted: v })}
          />
        </div>
      </Card>

      <Card>
        <CardHeader title="Reset" />
        <div className="flex items-center justify-between gap-4">
          <p className="text-small text-text-secondary">
            Removes this server's Dem AI overrides so it goes back to the bot's defaults.
          </p>
          <Button variant="secondary" size="md" onClick={handleReset} disabled={resetting || saving}>
            {resetting ? 'Resetting…' : 'Reset to defaults'}
          </Button>
        </div>
      </Card>

      <Card>
        <CardHeader title="Usage" action={<Gauge size={18} className="text-text-tertiary" aria-hidden="true" />} />
        <p className="text-small text-text-secondary">Usage charts and feedback are not yet exposed through the Bridge (read-only reporting, no write path needed — left for a follow-up pass).</p>
      </Card>

      <Card>
        <CardHeader title="Looking for provider health, benchmarks, or cache controls?" />
        <div className="flex items-center gap-2 text-small text-text-secondary">
          <MessageCircle size={16} className="text-accent-ai-400 shrink-0" aria-hidden="true" />
          Those are bot-owner-only diagnostics and live in the Owner Console, not here — see docs/ARCHITECTURE.md §1.3.
        </div>
      </Card>

      <SaveBar
        visible={dirty || saving}
        onSave={handleSave}
        onDiscard={handleDiscard}
        saving={saving}
      />
    </div>
  );
}
